/**
 * Authentication error component
 * Displays error message when authentication fails
 */

import {
	Button,
	Card,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle,
} from "@medme/ui"
import { cn } from "@medme/ui/lib"
import { useTranslation } from "react-i18next"

export interface AuthErrorProps {
	/** Error message to display */
	message?: string
	/** Error code (e.g., 401, 400) */
	errorCode?: number | string
	/** Callback for retry action */
	onRetry?: () => void
	/** Callback for reload action */
	onReload?: () => void
	className?: string
}

/**
 * Component that displays authentication error with user-friendly message
 * Provides actionable information like retry option
 */
export function AuthError({
	message,
	errorCode,
	onRetry,
	onReload,
	className,
}: AuthErrorProps) {
	const { t } = useTranslation()
	const defaultMessage =
		errorCode === 401
			? t("auth.error.session-expired")
			: errorCode === 400
				? t("auth.error.invalid-credentials")
				: t("auth.error.error-occurred")

	const displayMessage = message || defaultMessage

	const handleReload = () => {
		if (onReload) {
			onReload()
		} else {
			window.location.reload()
		}
	}

	return (
		<div
			className={cn(
				"min-h-screen flex items-center justify-center bg-background p-4",
				className,
			)}
		>
			<Card className="w-full max-w-md">
				<CardHeader>
					<CardTitle className="text-destructive">
						{t("auth.error.title")}
					</CardTitle>
					{errorCode && (
						<CardDescription>
							{t("auth.error.error-code")}: {errorCode}
						</CardDescription>
					)}
				</CardHeader>
				<CardContent className="space-y-4">
					<p className="text-muted-foreground">{displayMessage}</p>
					<div className="flex flex-col sm:flex-row gap-2">
						{onRetry && (
							<Button onClick={onRetry} variant="default" className="flex-1">
								{t("auth.error.retry")}
							</Button>
						)}
						<Button
							onClick={handleReload}
							variant={onRetry ? "outline" : "default"}
							className="flex-1"
						>
							{t("auth.error.reload")}
						</Button>
					</div>
				</CardContent>
			</Card>
		</div>
	)
}
